import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../shared/DashboardLayout';
import { useAuth } from '../../context/AuthContext';   // ← ADD
import { grievanceAPI } from '../../services/api';
import '../../styles/Grievance.css';

// ── maps each role to its home dashboard ─────────────────────────────────────
const DASHBOARD_URL = {
  GRIEVANCE_OFFICER:     '/grievance-officer-dashboard',
  ADMIN:                 '/admin-dashboard',
  STUDENT:               '/student-dashboard',
  FACULTY:               '/faculty-dashboard',
  HOD:                   '/hod-dashboard',
  FINANCE:               '/finance-dashboard',
  HOSTEL_ADMIN:          '/hostel-dashboard',
  LIBRARY_STAFF:         '/library-dashboard',
  TRANSPORT_MANAGER:     '/transport-dashboard',
  recepsonist:           '/reception',
  LAB_TECH_MICROBIOLOGY: '/lab-dashboard',
  LAB_TECH_PATHOLOGY:    '/lab-dashboard',
  LAB_TECH_BIOCHEMISTRY: '/lab-dashboard',
  BILLING_MEMBER:        '/lab-billing',
  BILLING_MANAGER:       '/billing-manager',
  SAMPLE_COLLECTOR:      '/sample-collection',
  REPORT_GENERATION:     '/report-generation',
};

// 👇 Place AFTER the object
const getDashboardUrl = (user) => {
  if (user.role === 'FACULTY' && user.subrole === 'HEAD_OF_DEPARTMENT') {
    return '/hod-dashboard';
  }

  return DASHBOARD_URL[user.role] || '/login';
};

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'PENDING',      label: 'Pending' },
  { value: 'ASSIGNED',     label: 'Assigned' },
  { value: 'UNDER_REVIEW', label: 'Under Review' },
  { value: 'RESOLVED',     label: 'Resolved' },
  { value: 'REJECTED',     label: 'Rejected' },
  { value: 'ESCALATED',    label: 'Escalated' },
  { value: 'CLOSED',       label: 'Closed' },
  { value: 'REOPENED',     label: 'Reopened' },
];

const STATUS_COLORS = {
  PENDING:      'badge-pending',
  ASSIGNED:     'badge-assigned',
  UNDER_REVIEW: 'badge-under_review',
  RESOLVED:     'badge-resolved',
  REJECTED:     'badge-rejected',
  ESCALATED:    'badge-escalated',
  CLOSED:       'badge-closed',
  REOPENED:     'badge-reopened',
};

const PRIORITY_COLORS = {
  LOW:    'badge-low',
  MEDIUM: 'badge-medium',
  HIGH:   'badge-high',
};


const MyGrievancesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();   // ← ADD

  const [grievances,    setGrievances]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [statusFilter,  setStatusFilter]  = useState('');
  const [search,        setSearch]        = useState('');

  useEffect(() => { loadGrievances(); }, [statusFilter]);

  const loadGrievances = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const data = await grievanceAPI.getMine(params);
      setGrievances(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error(err);
      setGrievances([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = grievances.filter(g => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      g.grievance_id.toLowerCase().includes(q) ||
      g.subject.toLowerCase().includes(q) ||
      g.category.toLowerCase().includes(q)
    );
  });

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  const timeAgo = (d) => {
    const secs = Math.floor((Date.now() - new Date(d)) / 1000);
    if (secs < 60)    return 'Just now';
    if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  };

  return (
    <DashboardLayout title="My Grievances">
      <div className="grievance-container">

        {/* ── Back to dashboard ───────────────────────────────────────────── */}
        <button
          onClick={() => navigate(getDashboardUrl(user?.role))}
          style={{
            background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
            marginBottom: '1rem', padding: 0, fontSize: '0.875rem',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>

        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
              My Grievances
            </h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
              {filtered.length} grievance{filtered.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="grievance-filters">
          <input
            className="grievance-search"
            placeholder="Search by ID, subject, category…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                className={`filter-chip ${statusFilter === f.value ? 'active' : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── List ────────────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner-large" />
            <p style={{ color: '#6b7280', marginTop: '0.75rem' }}>Loading grievances…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="grievance-empty">
            <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No grievances found</p>
            <span>Click "+ New Grievance" to raise an issue.</span>
          </div>
        ) : (
          filtered.map(g => (
            <div
              key={g.id}
              className="grievance-card"
              onClick={() => navigate(`/grievances/${g.id}`)}
            >
              <div className="grievance-card-header">
                <div style={{ flex: 1 }}>
                  <p className="grievance-subject">{g.subject}</p>
                  <div className="grievance-card-meta">
                    <span className="grievance-id">{g.grievance_id}</span>
                    <span className={`grievance-badge ${STATUS_COLORS[g.status] || ''}`}>
                      {g.status.replace('_', ' ')}
                    </span>
                    <span className={`grievance-badge ${PRIORITY_COLORS[g.priority] || ''}`}>
                      {g.priority}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{g.category_display}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{timeAgo(g.updated_at)}</div>
                  <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginTop: 2 }}>
                    {formatDate(g.created_at)}
                  </div>
                  {g.response_count > 0 && (
                    <div style={{ fontSize: '0.75rem', color: '#6366f1', marginTop: 4 }}>
                      {g.response_count} response{g.response_count !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
              {g.department_assigned && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                  Assigned to: <strong>{g.department_assigned.replace(/_/g, ' ')}</strong>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyGrievancesPage;